import { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Edit,
  Copy,
  Archive,
  Eye,
  EyeOff,
  AlertCircle,
  Play,
  Trash2,
  Tag,
  FlaskConical,
  Wand2,
} from 'lucide-react';
import { cn, MathText } from '@jeraisonne/ui';
import { SUBJECTS, SCHOOL_LEVELS } from '@jeraisonne/db';
import type { SubjectSlug, SchoolLevel } from '@jeraisonne/db';
import { useExerciseDetail } from '@/lib/admin/exercise-queries';
import { canEditExercise } from '@/lib/admin/exercise-access';
import { useExerciseThemes, type ExerciseThemeRow } from '@/lib/admin/exercise-themes';
import {
  useDeleteExercise,
  useDuplicateExercise,
  useUpdateExerciseStatus,
} from '@/lib/admin/exercise-mutations';
import { MarkdownMath } from '@jeraisonne/ui';
import { VisualBlocksRenderer } from '@jeraisonne/ui';
import { useAuth, isAdmin } from '@/lib/auth';
import { isLiveTester } from '@/lib/live-tester';
import { useLiveChallengeForExercise } from '@/lib/live-test';
import { ExerciseReelCard } from '@/features/exercise-reel/exercise-reel-card';
import { ExerciseCardsChips } from '@/features/cards/exercise-cards-chips';
import { useExerciseV2 } from '@/lib/exercise-v2/api';
import { v2GenerationState, allVisualsOf } from '@/lib/exercise-v2/block-helpers';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));
const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const DIFFICULTY_META = {
  easy: { label: 'Facile', cls: 'bg-xp-50    text-xp-600    border-xp-100' },
  medium: { label: 'Moyen', cls: 'bg-level-50  text-level-600 border-level-100' },
  hard: { label: 'Difficile', cls: 'bg-accent-50 text-accent-600 border-accent-100' },
} as const;

/**
 * Méta d'affichage par statut éditorial. Doit couvrir TOUTES les valeurs de
 * l'enum DB `content_status` (cf. migration 0087 qui a ajouté `awaiting_review`).
 * Une valeur manquante ferait planter le rendu sur `stat.cls`.
 */
const STATUS_META = {
  draft: { label: 'Brouillon', cls: 'bg-ink-100 text-ink-600' },
  published: { label: 'Publié', cls: 'bg-xp-50   text-xp-600' },
  archived: { label: 'Archivé', cls: 'bg-ink-50  text-ink-400' },
  awaiting_review: { label: 'En relecture', cls: 'bg-accent-50 text-accent-600' },
} as const;

export function TutorExerciseDetailPage() {
  const { exerciseId } = useParams({ strict: false }) as { exerciseId: string };
  const navigate = useNavigate();
  const { data: exercise, isLoading } = useExerciseDetail(exerciseId);
  const { data: themes } = useExerciseThemes(exerciseId);
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  // Un exercice source d'un défi Live School se teste « en live » (chrono, drip
  // des clés) plutôt qu'en aperçu — réservé aux testeurs (cf. `live-test`).
  const canLiveTest = isLiveTester(profile, user?.email);
  const { data: liveChallenge } = useLiveChallengeForExercise(exerciseId, canLiveTest);
  const v2Query = useExerciseV2(exercise ? exerciseId : null);
  const v2Blocks = v2Query.data?.blocks ?? null;
  const deleteExercise = useDeleteExercise();
  const duplicateExercise = useDuplicateExercise();
  const updateStatus = useUpdateExerciseStatus();
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  async function handleDelete() {
    try {
      await deleteExercise.mutateAsync(exerciseId);
      navigate({ to: '/tutor/exercises' });
    } catch (err) {
      setDeleteConfirm(false);
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    }
  }

  async function handleDuplicate() {
    try {
      const newId = await duplicateExercise.mutateAsync(exerciseId);
      navigate({ to: '/tutor/exercises/$exerciseId', params: { exerciseId: newId } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la duplication');
    }
  }

  async function handleTogglePublish() {
    if (!exercise) return;
    const next = exercise.status === 'published' ? 'draft' : 'published';
    try {
      await updateStatus.mutateAsync({ exerciseId, status: next });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du changement de statut');
    }
  }

  async function handleArchive() {
    if (!confirm('Archiver cet exercice ?')) return;
    try {
      await updateStatus.mutateAsync({ exerciseId, status: 'archived' });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'archivage");
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <div className="bg-ink-100 mb-6 h-6 w-32 animate-pulse rounded-lg" />
        <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <Link
          to="/tutor/exercises"
          className="text-ink-500 hover:text-ink-900 mb-6 inline-flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Mes exercices
        </Link>
        <p className="text-ink-500 font-sans text-sm">Exercice introuvable.</p>
      </div>
    );
  }

  // Un exercice publié d'un AUTRE auteur (trouvé via la recherche globale) est
  // en lecture seule : on masque toutes les actions mutantes, on garde « Tester ».
  const canEdit = canEditExercise(exercise.created_by, user?.id, admin);

  // V1 (exercise.visual_blocks, colonne top-level) et V2 (data.visuals par bloc)
  // sont deux modèles distincts — on les fusionne à l'affichage pour que la
  // page de gestion montre TOUJOURS les visuels présents, quel que soit le
  // modèle de contenu (le Blueprint paramétrique n'écrit que du V2).
  const allVisuals = [...exercise.visual_blocks, ...(v2Blocks ? allVisualsOf(v2Blocks) : [])];

  const diff = DIFFICULTY_META[exercise.difficulty];
  const stat = STATUS_META[exercise.status] ?? STATUS_META.draft;
  const chapterLabel = exercise.chapter
    ? `${SUBJECT_LABEL[exercise.chapter.subject as SubjectSlug] ?? exercise.chapter.subject} · ${LEVEL_LABEL[exercise.chapter.level as SchoolLevel] ?? exercise.chapter.level} — ${exercise.chapter.title}`
    : null;
  const hasCover = !!exercise.illustration_url;
  const hasThemes = !!themes && themes.length > 0;

  const badges = (
    <>
      <span
        className={cn(
          'font-display inline-flex h-5 rounded-full border px-2 text-[10px] font-bold uppercase tracking-wide',
          diff.cls,
        )}
      >
        {diff.label}
      </span>
      <span
        className={cn(
          'font-display inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide',
          stat.cls,
        )}
      >
        {stat.label}
      </span>
      {exercise.step_by_step_mode && (
        <span className="font-display bg-primary-100 text-primary-700 inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
          Pas à pas
        </span>
      )}
    </>
  );

  return (
    <>
      <div className="pb-safe mx-auto max-w-3xl p-4 sm:p-6">
        {exercise.source_epreuve_id ? (
          <a
            href={`/tutor/admin/epreuves/${exercise.source_epreuve_id}`}
            className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'épreuve
          </a>
        ) : (
          <Link
            to="/tutor/exercises"
            className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Mes exercices
          </Link>
        )}

        {hasCover && (
          <div className="border-ink-100 relative mb-5 overflow-hidden rounded-2xl border">
            <img
              src={exercise.illustration_url!}
              alt=""
              loading="lazy"
              className="aspect-2/1 w-full object-cover"
            />
            <div className="bg-linear-to-t pointer-events-none absolute inset-x-0 bottom-0 h-2/3 from-black/70 to-transparent" />
            <div className="absolute left-2.5 top-2.5 flex flex-wrap items-center gap-1.5">
              {badges}
            </div>
            {hasThemes && (
              <div className="absolute inset-x-2.5 bottom-2.5 flex flex-wrap gap-1.5">
                {themes!.map((row) => (
                  <ThemeChip key={row.theme_id} row={row} onCover />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            {!hasCover && <div className="mb-2 flex flex-wrap items-center gap-2">{badges}</div>}
            <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight">
              <MathText>{exercise.title}</MathText>
            </h1>
            {chapterLabel && <p className="text-ink-500 mt-1 font-sans text-xs">{chapterLabel}</p>}
            <p className="text-ink-400 mt-0.5 font-sans text-xs">
              {v2Blocks
                ? (() => {
                    const s = v2GenerationState(v2Blocks);
                    return `${s.questions.length} question${s.questions.length !== 1 ? 's' : ''} · ${s.corrections.length} corrigé${s.corrections.length !== 1 ? 's' : ''} · ${s.distractorsCount} distracteur${s.distractorsCount !== 1 ? 's' : ''}`;
                  })()
                : 'Chargement du contenu…'}
              {exercise.estimated_minutes && ` · ${exercise.estimated_minutes} min`}
            </p>
            {exercise.generator_blueprint_id && (
              <Link
                to="/tutor/blueprints/$blueprintId"
                params={{ blueprintId: exercise.generator_blueprint_id }}
                className="text-primary-600 hover:text-primary-800 mt-1 inline-flex items-center gap-1 font-sans text-xs font-semibold transition-colors"
              >
                <Wand2 className="h-3.5 w-3.5" />
                Exercice paramétrique — voir le générateur
              </Link>
            )}
            {!hasCover && hasThemes && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {themes!.map((row) => (
                  <ThemeChip key={row.theme_id} row={row} onCover={false} />
                ))}
              </div>
            )}
            <ExerciseCardsChips exerciseId={exercise.id} />
          </div>
        </div>

        {!canEdit && (
          <div className="border-ink-200 bg-ink-50 mb-6 flex items-start gap-2 rounded-xl border px-4 py-3">
            <Eye className="text-ink-400 mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-ink-600 font-sans text-sm">
              Exercice d'un autre auteur — <span className="font-semibold">lecture seule</span>. Tu
              peux le consulter et le tester, mais pas le modifier.
            </p>
          </div>
        )}

        <div className="mb-6 flex flex-wrap gap-2">
          {canEdit && (
            <a
              href={`/tutor/exercises/${exerciseId}/edit-v2`}
              className="font-display inline-flex h-9 items-center gap-1.5 rounded-xl bg-emerald-500 px-4 text-sm font-bold text-white transition-all hover:bg-emerald-600 active:scale-[.98]"
            >
              <Edit className="h-3.5 w-3.5" /> Modifier
            </a>
          )}
          {canEdit && (
            <>
              <button
                onClick={handleTogglePublish}
                disabled={updateStatus.isPending}
                className="border-ink-200 text-ink-700 font-display hover:border-ink-300 inline-flex h-9 items-center gap-1.5 rounded-xl border bg-white px-4 text-sm font-bold transition-all active:scale-[.98] disabled:opacity-50"
              >
                {exercise.status === 'published' ? (
                  <>
                    <EyeOff className="h-3.5 w-3.5" /> Dépublier
                  </>
                ) : (
                  <>
                    <Eye className="h-3.5 w-3.5" /> Publier
                  </>
                )}
              </button>
              <button
                onClick={handleDuplicate}
                disabled={duplicateExercise.isPending}
                className="border-ink-200 text-ink-700 font-display hover:border-ink-300 inline-flex h-9 items-center gap-1.5 rounded-xl border bg-white px-4 text-sm font-bold transition-all active:scale-[.98] disabled:opacity-50"
              >
                <Copy className="h-3.5 w-3.5" /> Dupliquer
              </button>
              {exercise.status !== 'archived' && (
                <button
                  onClick={handleArchive}
                  disabled={updateStatus.isPending}
                  className="border-ink-200 text-ink-500 font-display hover:border-ink-300 inline-flex h-9 items-center gap-1.5 rounded-xl border bg-white px-4 text-sm font-bold transition-all active:scale-[.98] disabled:opacity-50"
                >
                  <Archive className="h-3.5 w-3.5" /> Archiver
                </button>
              )}
            </>
          )}
          {liveChallenge ? (
            <a
              href={`/live/${liveChallenge.level}?test=${liveChallenge.id}`}
              className="border-accent-200 bg-accent-50 text-accent-700 font-display hover:bg-accent-100 inline-flex h-9 items-center gap-1.5 rounded-xl border px-4 text-sm font-bold transition-all active:scale-[.98]"
            >
              <FlaskConical className="h-3.5 w-3.5" /> Tester en live
            </a>
          ) : (
            <Link
              to="/tutor/exercises/$exerciseId/test"
              params={{ exerciseId }}
              className="border-primary-200 bg-primary-50 text-primary-700 font-display hover:bg-primary-100 inline-flex h-9 items-center gap-1.5 rounded-xl border px-4 text-sm font-bold transition-all active:scale-[.98]"
            >
              <Play className="h-3.5 w-3.5" /> Tester
            </Link>
          )}
          {canEdit &&
            (deleteConfirm ? (
              <div className="border-error-200 bg-error-50 ml-auto flex items-center gap-2 rounded-xl border px-3 py-1.5">
                <span className="text-error-700 font-display text-xs font-semibold">
                  Supprimer définitivement ?
                </span>
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="border-ink-200 text-ink-600 font-display hover:border-ink-300 inline-flex h-7 items-center rounded-lg border bg-white px-3 text-xs font-bold transition-all active:scale-[.98]"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteExercise.isPending}
                  className="bg-error-600 hover:bg-error-700 font-display inline-flex h-7 items-center gap-1 rounded-lg px-3 text-xs font-bold text-white transition-all active:scale-[.98] disabled:opacity-50"
                >
                  <Trash2 className="h-3 w-3" />
                  {deleteExercise.isPending ? 'Suppression…' : 'Confirmer'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="border-error-100 bg-error-50 text-error-600 font-display hover:bg-error-100 ml-auto inline-flex h-9 items-center gap-1.5 rounded-xl border px-4 text-sm font-bold transition-all active:scale-[.98]"
              >
                <Trash2 className="h-3.5 w-3.5" /> Supprimer
              </button>
            ))}
        </div>

        {error && (
          <div className="bg-error-50 border-error-100 mb-4 flex items-start gap-2 rounded-xl border px-4 py-3">
            <AlertCircle className="text-error-600 mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-error-700 font-sans text-sm">{error}</p>
          </div>
        )}

        {/* Diffusion Reel Instagram (admin uniquement) */}
        {admin && (
          <ExerciseReelCard
            exerciseId={exercise.id}
            title={exercise.title}
            level={exercise.chapter?.level ?? null}
            subject={exercise.chapter?.subject ?? null}
            chapterSlug={exercise.chapter?.slug ?? null}
          />
        )}

        {exercise.context_md && (
          <div className="border-ink-200 mb-6 rounded-2xl border bg-white p-5">
            <p className="font-display text-ink-500 mb-2 text-[11px] font-semibold uppercase tracking-[.1em]">
              Contexte commun
            </p>
            <div className="prose prose-sm max-w-none">
              <MarkdownMath>{exercise.context_md}</MarkdownMath>
            </div>
          </div>
        )}

        <div className="border-ink-200 mb-6 rounded-2xl border bg-white p-5">
          <p className="font-display text-ink-500 mb-2 text-[11px] font-semibold uppercase tracking-[.1em]">
            Énoncé complet
          </p>
          <div className="prose prose-sm max-w-none">
            <MarkdownMath>{exercise.statement_md}</MarkdownMath>
          </div>
        </div>

        {allVisuals.length > 0 && (
          <div className="mb-6">
            <p className="font-display text-ink-500 mb-3 text-[11px] font-semibold uppercase tracking-[.1em]">
              Éléments visuels
            </p>
            <VisualBlocksRenderer blocks={allVisuals} />
          </div>
        )}

        <V2ContentSummary exerciseId={exerciseId} blocks={v2Blocks} loading={v2Query.isPending} />
      </div>
    </>
  );
}

/**
 * Résumé compact du contenu V2 sur la page de détail : une ligne par question
 * avec son état (corrigé / distracteurs présents ou manquants) + un CTA vers
 * l'éditeur V2 où la génération assistée se fait. Le détail complet (étapes du
 * corrigé, distracteurs) reste dans l'éditeur.
 */
function V2ContentSummary({
  exerciseId,
  blocks,
  loading,
}: {
  exerciseId: string;
  blocks: ExerciseBlock[] | null;
  loading: boolean;
}) {
  if (loading || !blocks) {
    return (
      <div className="border-ink-200 bg-ink-50 h-24 animate-pulse rounded-2xl border" aria-busy />
    );
  }

  const s = v2GenerationState(blocks);
  const missingCorrectionIds = new Set(s.questionsWithoutCorrection.map((q) => q.id));
  // Questions dont au moins un corrigé QCM est sans distracteur.
  const qcmMissingParentIds = new Set(s.qcmWithoutDistractors.map((c) => c.parent_id));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-ink-500 text-[11px] font-bold uppercase tracking-[.1em]">
          Questions ({s.questions.length})
        </h2>
        <a
          href={`/tutor/exercises/${exerciseId}/edit-v2`}
          className="font-display inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-500 px-3 text-xs font-bold text-white transition-all hover:bg-emerald-600 active:scale-[.98]"
        >
          <Edit className="h-3.5 w-3.5" /> Compléter dans l'éditeur V2
        </a>
      </div>

      {s.questions.length === 0 ? (
        <p className="border-ink-200 text-ink-500 rounded-2xl border border-dashed bg-white px-4 py-6 text-center font-sans text-sm">
          Aucune question. Ouvre l'éditeur V2 pour construire l'exercice.
        </p>
      ) : (
        <ol className="space-y-2">
          {s.questions.map((q, i) => {
            const hasCorrection = !missingCorrectionIds.has(q.id);
            const distractorsMissing = qcmMissingParentIds.has(q.id);
            const prompt = typeof q.data.prompt_md === 'string' ? (q.data.prompt_md as string) : '';
            return (
              <li
                key={q.id}
                className="border-ink-200 flex items-start gap-3 rounded-2xl border bg-white p-4"
              >
                <span className="bg-ink-100 text-ink-600 font-display mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-ink-800 prose prose-sm max-w-none font-sans text-sm">
                    <MarkdownMath>{prompt}</MarkdownMath>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <SummaryBadge
                      ok={hasCorrection}
                      label={hasCorrection ? 'Corrigé' : 'Sans corrigé'}
                    />
                    {hasCorrection && (
                      <SummaryBadge
                        ok={!distractorsMissing}
                        label={distractorsMissing ? 'Distracteurs manquants' : 'Distracteurs'}
                      />
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function SummaryBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-sans text-[11px] font-semibold',
        ok ? 'bg-xp-50 text-xp-600' : 'bg-accent-50 text-accent-600',
      )}
    >
      {ok ? '✓' : '•'} {label}
    </span>
  );
}

function ThemeChip({ row, onCover }: { row: ExerciseThemeRow; onCover: boolean }) {
  return (
    <Link
      to="/themes/$slug"
      params={{ slug: row.theme.slug }}
      className={cn(
        'inline-flex max-w-44 items-center gap-1.5 rounded-lg border py-0.5 pr-2 font-sans text-xs transition-colors',
        row.theme.illustration_url ? 'pl-0.5' : 'pl-2',
        onCover
          ? 'border-white/25 bg-black/55 text-white backdrop-blur-sm hover:bg-black/70'
          : 'bg-primary-50 text-primary-700 border-primary-100 hover:bg-primary-100',
      )}
    >
      {row.theme.illustration_url ? (
        <img
          src={row.theme.illustration_url}
          alt=""
          loading="lazy"
          className="h-5 w-5 shrink-0 rounded object-cover"
        />
      ) : (
        <Tag className="h-3 w-3 shrink-0" />
      )}
      <span className="truncate">{row.theme.title}</span>
    </Link>
  );
}
